import UserIcon from '@assets/CustomIcons/user_edited.svg'
import ModelGenIcon from '@assets/Logos/model-generated-icon.svg'
import '@components/Model/model.css'
import { GraviGrid } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { Col, Row } from 'antd'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LastModelRunResults } from '../Model/Configuration/Configuration'
import { Logs } from '../Model/Log/Logs'
import { getColumnDefs } from './supplyLogColumnDefs'
import { SupplyHistoryActions } from './CellRenderers/SupplyHistoryActions'
import { SupplyHistoryStatus } from './CellRenderers/SupplyHistoryStatus'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useUserSettings } from '@hooks/settings'

export const SupplyLog = () => {
  const gridRef = useRef()

  const [isLoading, setIsLoading] = useState(true)
  const [isLogsLoading, setIsLogsLoading] = useState(true)

  const [cancelLoadingState, setCancelLoadingState] = useState({
    loading: false,
    historyId: '',
  })

  const [rowData, setRowData] = useState([])

  const [status, setStatus] = useState('')
  const [solverState, setSolverState] = useState()
  const [startRunDate, setStartRunDate] = useState()
  const [lastRunDate, setLastRunDate] = useState()
  const [historyId, setHistoryId] = useState('')

  useEffect(() => {
    setIsLogsLoading(true)
    if (historyId) {
      api
        .getSmartSupplyState(historyId)
        .then((resp) => {
          const { data } = resp
          setSolverState(data)
        })
        .finally(() => setTimeout(() => setIsLogsLoading(false), 500))
    }
  }, [historyId])

  useEffect(() => {
    getModalRunStatusAndDetails()
  }, [])

  const getModalRunStatusAndDetails = () => {
    setIsLoading(true)
    api
      .getSmartSupplyState()
      .then((resp) => {
        const { data } = resp
        setLastRunDate(formatters.DATE_TIME(moment(data.last_run)))
        setStatus(data.last_status || data.latest_runs[0].status)
        setSolverState(data)
        setRowData(data.latest_runs)
        setSolverState(data)
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false)
          setIsLogsLoading(false)
        }, 500)
      })
  }

  const onRunCancel = (smart_supply_id) => {
    setCancelLoadingState({ loading: true, historyId: smart_supply_id })
    api
      .cancelSmartSupply(smart_supply_id)
      .catch(() => notification.error({ message: 'Failed to cancel smart supply' }))
      .finally(() => {
        setCancelLoadingState((loadingState) => ({ ...loadingState, loading: false }))
        getModalRunStatusAndDetails()
      })
  }

  const onCellClicked = useCallback((e) => {
    const col = e.column.colId
    const id = e.data.id

    if (col !== 'actions' && historyId !== id) {
      setHistoryId(id)
    }
  }, [])

  const { updateSetting } = useUserSettings()

  const columnDefs = useMemo(() => getColumnDefs({ cancelLoadingState, onRunCancel }), [cancelLoadingState])
  const getRowId = useCallback((row) => row?.data.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      frameworkComponents: {
        SupplyHistoryActions,
        SupplyHistoryStatus,
      },
      suppressRowClickSelection: true,
      onCellClicked,
      rowSelection: 'single',
    }),
    []
  )

  if (status === '' || !solverState) return null

  return (
    <div className='model-run-container p-3'>
      <Row className='flex'>
        <Col flex={1} className={`flex-1`} style={{ display: 'flex', flexDirection: 'column' }}>
          <span className='section-title'>MODEL STATUS</span>
          <div className='run-new-model-container mt-2' style={{ flex: 1 }}>
            <LastModelRunResults
              solverState={solverState}
              status={status}
              startRunDateTime={startRunDate}
              lastRunDateTime={lastRunDate}
              isSupply
            />
          </div>
        </Col>
        <Col span={18}>
          <div className='flex-1'>
            <span className='section-title mt-1 pl-3'>LOGS</span>
            <Logs logs={solverState?.log_data} isLoading={isLogsLoading} isSupply />
          </div>
        </Col>
      </Row>

      <Row style={{ height: 600 }} className='mt-3'>
        <Col span={24}>
          <GraviGrid
            externalRef={gridRef}
            columnDefs={columnDefs}
            controlBarProps={{
              title: 'Smart Supply History',
            }}
            sideBar={false}
            agPropOverrides={agPropOverrides}
            storageKey='smartSupplyHistoryGrid'
            rowData={rowData}
            loading={isLoading}
            onGridConfigChanged={(config) => updateSetting('gridConfig::smartSupplyHistoryGrid', config)}
          />
        </Col>
      </Row>
    </div>
  )
}
